import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { AtsSkeleton, Card, Tooltip } from '_atoms';
import { Priority } from '_atoms/Priority';
import type { PriorityValue } from '_atoms/Priority';
import { Typography, TYPOGRAPHY_WEIGHT } from '_atoms/Typography';
import { Status } from '_atoms/Status';
import { OPENING_STATUS_TYPES } from 'constants/opening';
import { ROLES } from 'permission';
import { getTLActiveOpeningsDashboardAPI } from 'actions';
import { getUser } from 'store/selectors/authorizeSelectors';
import { ReactComponent as JobImage } from 'dist/emptyStates/dashboardOpening.svg';
import './style.scss';
import type { DashboardTeamLeadOpening } from 'actions/dashboardActions';

export const ActiveOpenings: React.FC = () => {
  const { t } = useTranslation();
  const user = useSelector(getUser);
  const showServices = user.roles.filter(r => r.name === ROLES.TEAM_LEAD).length > 1;

  const [openings, setOpenings] = useState<DashboardTeamLeadOpening[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    getTLActiveOpeningsDashboardAPI({})
      .then(resp => setOpenings(resp as unknown as DashboardTeamLeadOpening[]))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Card className="active-openings">
        <div className="active-openings__header">
          {t('translation:active_openings.title')}
          <span className="number">({openings.length})</span>
        </div>
        <div className="active-openings__content-loading">
          <AtsSkeleton height={140} />
        </div>
      </Card>
    );
  }

  if (!openings.length) {
    return (
      <Card className="active-openings">
        <div className="active-openings__header">
          {t('translation:active_openings.title')}
          <span className="number">0</span>
        </div>
        <div className="active-openings__content-empty">
          <JobImage className="empty-image" />
          <Typography.Title3 weight={TYPOGRAPHY_WEIGHT.BOLD}>
            {t('translation:active_openings.text_empty')}
          </Typography.Title3>
        </div>
      </Card>
    );
  }

  return (
    <Card className="active-openings">
      <div className="active-openings__header">
        {t('translation:active_openings.title')}
        <span className="number">{openings.length}</span>
      </div>
      <div className="active-openings__content">
        {openings.map(opening => (
          <div key={opening.uuid} className="active-opening">
            <Priority priority={opening.priority as PriorityValue} />
            <div className="active-opening__info">
              <Tooltip label={`${opening.level} ${opening.job_name}`}>
                <a
                  href={`/jobs/${opening.job_uuid}/show/openings?scroll=${opening.uuid}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {`${opening.level} ${opening.job_name}`}
                </a>
              </Tooltip>
              {showServices && (
                <Typography.Caption className="active-opening__service">{opening.service_name}</Typography.Caption>
              )}
            </div>
            <Status type={OPENING_STATUS_TYPES[opening.status]}>{opening.status}</Status>
          </div>
        ))}
      </div>
    </Card>
  );
};
